import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Cloud, FileText, FolderArchive, ShieldCheck, Siren, Smartphone, Trash2 } from "lucide-react";
import { BRAND } from "../../lib/api";
import { clearDeviceCopy, openBlob, readEmergency, readIndex, readPdf, storageInfo } from "../../lib/safeguardCache";
import { itDate, itDateTime, useOnline } from "../../components/SafeGuardArchive";
import { Button, Card, Spinner } from "../../components/ui-kit";
import DeviceRecords from "../../components/DeviceRecords";

// Ispezione ASL/NAS di emergenza: nessuna chiamata al server, solo i documenti
// già salvati su questo dispositivo (verificati con SHA-256 all'apertura) e il
// link all'archivio di sicurezza su DF Drive.
export default function OfflineInspection() {
  const online = useOnline();
  const [index, setIndex] = useState(undefined);
  const [storage, setStorage] = useState(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => { readIndex().then(setIndex); storageInfo().then(setStorage); }, []);

  const open = async (doc) => {
    const win = window.open("", "_blank");
    try {
      const result = await readPdf(doc);
      if (!result.ok) {
        if (win) win.close();
        setProblem(result.reason === "hash"
          ? `Il documento ${doc.doc_number} salvato su questo dispositivo non supera la verifica di integrità: non viene mostrato.`
          : `Il documento ${doc.doc_number} non è più presente su questo dispositivo.`);
        return;
      }
      setProblem(null);
      openBlob(result.blob, win);
    } catch {
      if (win) win.close();
      setProblem("Non è stato possibile aprire il documento su questo dispositivo.");
    }
  };

  const openEmergencyCopy = async () => {
    const win = window.open("", "_blank");
    const blob = await readEmergency();
    if (blob) { setProblem(null); openBlob(blob, win); return; }
    if (win) win.close();
    setProblem("La procedura di emergenza non è salvata su questo dispositivo.");
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere da questo dispositivo tutti i report salvati? Restano disponibili nell'app e su DF Drive.")) return;
    await clearDeviceCopy();
    setIndex(null);
  };

  const docs = index?.documents || [];
  const daily = docs.filter((d) => d.kind === "daily").sort((a, b) => b.period_key.localeCompare(a.period_key));
  const monthly = docs.filter((d) => d.kind === "monthly").sort((a, b) => b.period_key.localeCompare(a.period_key));

  return (
    <div className="min-h-screen bg-haccp-bg">
      <header className="bg-navy text-white">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4 sm:px-6">
          <img src={BRAND.symbol} alt="" className="h-9 w-9" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.18em] text-gold-bright">Modalità ispezione organi di controllo · emergenza</p>
            <p className="truncate font-heading text-lg">{index?.company_name || "DF HACCP"}</p>
          </div>
          <Link to="/login" className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-white/70 hover:text-white" data-testid="offline-back-login"><ArrowLeft className="h-4 w-4" />Accesso</Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6" data-testid="offline-inspection">
        <Card className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <Smartphone className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading text-2xl font-semibold text-navy">Documenti salvati su questo dispositivo</h1>
              <p className="mt-1 text-sm text-slate-600">
                Copie dei report archiviati da DF HACCP, consultabili anche senza Internet o con il server non raggiungibile.
                {index?.saved_at && <> Copia aggiornata il <strong>{itDateTime(index.saved_at)}</strong>.</>}
              </p>
              {!online && <p className="mt-2 text-sm font-medium text-amber-700">Il dispositivo è senza connessione.</p>}
            </div>
          </div>
          {daily[0] && (
            <div className="mt-5 rounded-xl border border-hairline bg-slate-50 p-4" data-testid="offline-covered-until">
              <p className="text-xs uppercase tracking-wider text-slate-400">Registrazioni documentate fino al</p>
              <p className="font-heading text-2xl font-semibold text-navy">{itDate(daily[0].period_key)}</p>
              <p className="text-xs text-slate-500">Documento {daily[0].doc_number} · dati salvati sul server fino al {itDateTime(daily[0].data_cutoff)}</p>
              <Button className="mt-3" onClick={() => open(daily[0])} data-testid="offline-open-last"><FileText className="h-4 w-4" />Apri e stampa l’ultimo report</Button>
            </div>
          )}
          {index?.emergency_saved_at && (
            <button type="button" onClick={openEmergencyCopy} data-testid="offline-emergency-pdf"
              className="mt-4 flex w-full items-center gap-4 rounded-xl bg-gradient-to-r from-red-600 to-red-700 px-4 py-3.5 text-left text-white shadow-lg shadow-red-900/20 ring-1 ring-red-400/40 transition-colors hover:from-red-500 hover:to-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white/15 ring-1 ring-white/25"><Siren className="h-6 w-6" strokeWidth={1.75} /></span>
              <span className="min-w-0 flex-1">
                <span className="block font-heading text-lg font-semibold leading-tight">Stampa la procedura di emergenza</span>
                <span className="mt-0.5 block text-xs text-red-50/90">Cosa fare se l’app non funziona, QR dell’archivio DF Drive e registro cartaceo</span>
              </span>
            </button>
          )}
          {problem && <p role="alert" className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{problem}</p>}
        </Card>

        {index === undefined && <Card className="grid place-items-center p-10"><Spinner /></Card>}
        {index === null && (
          <Card className="p-5 sm:p-6" data-testid="offline-empty">
            <p className="font-medium text-navy">Su questo dispositivo non ci sono documenti salvati.</p>
            <p className="mt-1 text-sm text-slate-600">La copia si crea da sola quando un utente dell’azienda usa l’app con la connessione attiva (non durante “Entra come” del consulente). Su iPhone e iPad la copia fatta in Safari e quella dell’app installata nella schermata Home sono separate: apri questa pagina dallo stesso punto in cui usi l’app. Nel frattempo usa l’archivio DF Drive indicato nell’email dei report o i moduli cartacei di emergenza.</p>
          </Card>
        )}

        {index?.share_url && (
          <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
            <Cloud className="hidden h-6 w-6 shrink-0 text-gold sm:block" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-navy">Archivio di sicurezza su DF Drive</p>
              <p className="text-sm text-slate-500">Separato dal server dell’app: contiene tutti i report copiati e verificati{index.last_copy_at ? ` (ultima copia ${itDateTime(index.last_copy_at)})` : ""}. Serve una connessione Internet.</p>
            </div>
            <a href={index.share_url} target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] items-center justify-center gap-2 px-5 text-sm rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 font-semibold text-white shadow-md shadow-emerald-900/20 ring-1 ring-emerald-400/40 transition-[transform,background-color] hover:-translate-y-0.5 hover:from-emerald-500 hover:to-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2" data-testid="offline-dfdrive-link"><Cloud className="h-4 w-4" />Apri DF Drive</a>
          </Card>
        )}

        {index?.tenant_id && <DeviceRecords canSync={false} tenantId={index.tenant_id} />}

        {daily.length > 0 && (
          <Card className="p-5 sm:p-6" data-testid="offline-daily-list">
            <h2 className="font-heading text-xl font-semibold">Report giornalieri ({daily.length})</h2>
            <ul className="mt-3 divide-y divide-hairline">
              {daily.map((doc) => (
                <li key={doc.id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="w-24 font-medium text-navy">{itDate(doc.period_key)}</span>
                  <span className="min-w-0 flex-1 text-xs text-slate-500">
                    {doc.counts?.registrations ?? 0} registrazioni
                    {doc.counts?.missing ? <span className="text-red-600"> · {doc.counts.missing} non registrati</span> : null}
                    {doc.version > 1 ? ` · versione ${doc.version}` : ""}
                  </span>
                  <Button variant="ghost" className="px-3" onClick={() => open(doc)} data-testid={`offline-open-${doc.period_key}`}><FileText className="h-4 w-4" />Apri</Button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {monthly.length > 0 && (
          <Card className="p-5 sm:p-6">
            <h2 className="font-heading text-xl font-semibold">Fascicoli mensili</h2>
            <ul className="mt-3 divide-y divide-hairline">
              {monthly.map((doc) => (
                <li key={doc.id} className="flex items-center gap-3 py-3">
                  <FolderArchive className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <span className="flex-1 font-medium text-navy">{doc.period_key.slice(5, 7)}/{doc.period_key.slice(0, 4)}</span>
                  <Button variant="ghost" className="px-3" onClick={() => open(doc)}><FileText className="h-4 w-4" />Apri</Button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="space-y-2 p-5 text-xs text-slate-500 sm:p-6">
          <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />Ogni documento viene ricontrollato con la sua impronta SHA-256 prima di essere aperto. L’impronta verifica l’integrità del file; non è una firma digitale.</p>
          <p>Questa pagina mostra solo documenti già archiviati: le registrazioni successive all’ultimo report non sono incluse.</p>
          {index && <button type="button" onClick={remove} className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-red-600 hover:underline" data-testid="offline-remove-copy"><Trash2 className="h-3.5 w-3.5" />Rimuovi i documenti da questo dispositivo</button>}
          {storage?.persisted === false && <p className="text-amber-700">Attenzione: il browser potrebbe cancellare questi documenti per liberare spazio. Su iPhone e iPad installa l’app nella schermata Home e aprila almeno una volta alla settimana.</p>}
        </Card>
      </main>
    </div>
  );
}
